import { Box, Divider, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import WorldIcon from 'assets/icons/mapIcons/world.svg';
import { PermissionBadge, hasResourcePermission, useUser } from 'modules/User';
import { ReactElement, memo, useMemo } from 'react';

import {
  ALL_MAP_CONFIGS,
  DEFAULT_MAP_CONFIGS,
  DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
} from './MapWidgetMenu.config';
import { getButtonConfigs } from './MapWidgetMenu.helpers';
import { MapButtonProps, SelectionGridProps } from './MapWidgetMenu.types';

const mapButtonFocus = {
  backgroundColor: 'gray.100',
  outline: '2px solid',
  outlineColor: 'upgrade.500',
};

const mapButtonHover = {
  backgroundColor: 'hover.gray',
};

const MapButton = memo(
  ({
    menuButtonConfig,
    onClick,
    permission,
    isAccessAllowed,
    buttonType,
    createWidgetData,
  }: MapButtonProps): ReactElement => {
    const onMapButtonClick = () => onClick(menuButtonConfig, buttonType);
    const iconPath = menuButtonConfig.icon ? menuButtonConfig.icon : WorldIcon;

    return (
      <Flex position={'relative'}>
        <Box
          as='button'
          width={'100%'}
          minHeight={28}
          borderRadius='base'
          borderColor='gray.100'
          padding={3}
          transitionDuration='100ms'
          outline='1px solid'
          outlineColor='gray.100'
          role='group'
          _hover={mapButtonHover}
          _focus={mapButtonFocus}
          onClick={onMapButtonClick}
        >
          {!isAccessAllowed && <PermissionBadge permission={permission} position={'absolute'} top={-2} right={2} />}
          <Flex direction={'column'} alignItems={'center'} height={'fit-content'} gap={2}>
            <Box height={'64px'}>
              <img src={iconPath} alt='' role='presentation' style={{ pointerEvents: 'none' }} />
            </Box>
            <Text fontWeight={'normal'} fontSize={'xs'} lineHeight={4}>
              {menuButtonConfig.label}
            </Text>
          </Flex>
        </Box>
      </Flex>
    );
  },
);
MapButton.displayName = 'MapButton';

export const SelectionGrid = memo(
  ({ onClick, onClickUpgrade, chosenTerm, mapCreationType, createWidgetData }: SelectionGridProps) => {
    const { userType } = useUser();

    const buttonConfig = useMemo(
      () => getButtonConfigs(chosenTerm, DEFAULT_MAP_CONFIGS, ALL_MAP_CONFIGS),
      [chosenTerm],
    );

    return (
      <>
        <SimpleGrid columns={2} spacing={3}>
          {buttonConfig.map((config, i) => {
            const isAccessAllowed = hasResourcePermission(
              userType,
              config.permission ? config.permission : DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
            );
            return (
              <MapButton
                key={i}
                onClick={isAccessAllowed ? onClick : onClickUpgrade}
                menuButtonConfig={config}
                permission={config.permission ? config.permission : DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION}
                isAccessAllowed={isAccessAllowed}
                buttonType={mapCreationType}
                createWidgetData={createWidgetData}
              />
            );
          })}
        </SimpleGrid>
        {chosenTerm && <Divider />}
      </>
    );
  },
);
SelectionGrid.displayName = 'SelectionGrid';
